/**
 * Floral hero controller
 * - Repeatable init/destroy lifecycle for Shopify Theme Editor section reloads.
 * - Pauses motion when hidden or outside the viewport.
 * - Avoids fixed section IDs and duplicate event listeners.
 */
(function () {
  "use strict";

  const ROOT_SELECTOR = "[data-floral-hero]";
  const INSTANCE_KEY = Symbol.for("floralHero.instance");
  const NAV_INSTANCE_KEY = Symbol.for("floralHero.navigation");
  const instances = new Set();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const parseNumber = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  class FloralHero {
    constructor(root) {
      this.root = root;
      this.hero = root.querySelector(".hero");
      this.camera = root.querySelector(".hero__camera");
      this.parallaxLayer = root.querySelector("[data-hero-parallax]");
      this.heroImage = root.querySelector("[data-hero-image], .hero__picture--mono img");
      this.monoPicture = root.querySelector(".hero__picture--mono");
      this.colorPicture = root.querySelector(".hero__picture--color");
      this.petalCaustic = root.querySelector("[data-petal-caustic]");
      this.lightLayer = root.querySelector(".hero__light");
      this.wakeBloom = root.querySelector("[data-wake-bloom]");
      this.washLayer = root.querySelector(".hero__wash");
      this.video = root.querySelector("[data-hero-video], .hero__video");
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.frame = 0;
      this.primeFrame = 0;
      this.readyFrame = 0;
      this.videoFrameCallback = 0;
      this.isVisible = true;
      this.pageIsVisible = !document.hidden;
      this.videoCompleted = false;
      this.currentX = 0;
      this.currentY = 0;
      this.targetX = 0;
      this.targetY = 0;
      this.pointerRect = null;
      this.narrativeStarted = false;
      this.detailStartedAt = 0;
      this.narrativeTimer = 0;
      this.narrativeDeadline = 0;
      this.narrativeRemaining = 0;
      this.narrativeTask = null;
      this.sequenceToken = 0;
      this.detailAnimations = new Set();
      this.sequenceAnimations = new Set();
      this.ambientAnimations = new Set();

      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const saveData = Boolean(navigator.connection && navigator.connection.saveData);
      const memory = navigator.deviceMemory || 8;
      const cores = navigator.hardwareConcurrency || 8;
      const motionSetting = root.dataset.motion || "auto";

      this.lowPower = saveData || memory <= 2 || cores <= 2;
      this.motionAllowed = motionSetting !== "off" && motionSetting !== "video" && !reducedMotion && !this.lowPower;
      this.videoPlaybackAllowed = Boolean(this.video) && motionSetting !== "off" && !reducedMotion && !this.lowPower;
      this.pointerMotionAllowed =
        this.motionAllowed &&
        window.matchMedia("(min-width: 900px) and (hover: hover) and (pointer: fine)").matches;
      this.parallaxStrength = clamp(parseNumber(root.dataset.parallaxStrength, 5), 0, 10);
      this.detailHold = clamp(parseNumber(root.dataset.detailHold, 2200), 1200, 6000);
      this.revealDuration = clamp(
        parseNumber(root.dataset.revealDuration, 2200),
        1600,
        4000
      );
      this.awakenDuration = clamp(
        parseNumber(root.dataset.awakenDuration, 1000),
        700,
        2200
      );
      this.videoTailDuration = clamp(parseNumber(root.dataset.videoTailDuration, 0.85), 0.5, 1.2);
      this.videoTailMinRate = clamp(parseNumber(root.dataset.videoTailMinRate, 0.35), 0.25, 0.65);

      this.root.style.setProperty("--detail-hold", `${this.detailHold}ms`);
      this.root.style.setProperty("--reveal-duration", `${this.revealDuration}ms`);
      this.root.style.setProperty("--awaken-duration", `${this.awakenDuration}ms`);
      this.root.classList.toggle("hero--enhanced", this.motionAllowed);
      this.root.classList.toggle("hero--static", !this.motionAllowed);
      this.root.dataset.heroStage = this.motionAllowed ? "detail" : "awake";

      this.bind();
      this.observe();
      this.markImageReady();
      this.markVideoReady();
      this.syncPlayback();
    }

    bind() {
      document.addEventListener("visibilitychange", () => this.onVisibilityChange(), {
        signal: this.signal,
      });

      if (this.video) {
        this.video.addEventListener("timeupdate", () => this.updateVideoPlaybackRate(), {
          signal: this.signal,
        });
        this.video.addEventListener("loadedmetadata", () => this.updateVideoPlaybackRate(), {
          once: true,
          signal: this.signal,
        });
        this.video.addEventListener("ended", () => {
          this.updateVideoPlaybackRate();
          this.videoCompleted = true;
          this.root.classList.add("hero--video-complete");
        }, { signal: this.signal });
      }

      if (!this.pointerMotionAllowed || !this.hero || !this.parallaxLayer) return;

      this.hero.addEventListener("pointerenter", () => this.cachePointerRect(), {
        passive: true,
        signal: this.signal,
      });
      this.hero.addEventListener("pointermove", (event) => this.onPointerMove(event), {
        passive: true,
        signal: this.signal,
      });
      this.hero.addEventListener("pointerleave", () => this.resetPointer(), {
        passive: true,
        signal: this.signal,
      });
      window.addEventListener("resize", () => this.cachePointerRect(), {
        passive: true,
        signal: this.signal,
      });
    }

    observe() {
      if (!("IntersectionObserver" in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isVisible = Boolean(entry && entry.isIntersecting);
          this.syncPlayback();
        },
        { threshold: 0.04 }
      );
      this.observer.observe(this.hero || this.root);
    }

    markImageReady() {
      if (!this.heroImage) {
        this.root.classList.add("hero--loaded");
        this.prepareNarrative();
        return;
      }

      const ready = () => {
        this.root.classList.add("hero--loaded");
        this.prepareNarrative();
      };

      if (this.heroImage.complete) {
        ready();
        return;
      }

      this.heroImage.addEventListener("load", ready, { once: true, signal: this.signal });
      this.heroImage.addEventListener("error", ready, { once: true, signal: this.signal });
    }

    markVideoReady() {
      if (!this.video || !this.videoPlaybackAllowed) return;

      let revealed = false;
      const reveal = () => {
        if (revealed) return;
        revealed = true;
        this.video.classList.add("is-ready");
        this.root.classList.add("hero--video-ready");
        this.startVideoRateMonitor();
        this.syncPlayback();
      };
      const ready = () => {
        if (typeof this.video.requestVideoFrameCallback === "function") {
          this.video.requestVideoFrameCallback(reveal);
        } else {
          reveal();
        }
      };
      const fallback = () => {
        this.video.classList.remove("is-ready");
        this.root.classList.remove("hero--video-ready");
      };

      if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        ready();
        return;
      }

      this.video.addEventListener("loadeddata", ready, { once: true, signal: this.signal });
      this.video.addEventListener("playing", ready, { once: true, signal: this.signal });
      this.video.addEventListener("error", fallback, { once: true, signal: this.signal });
    }

    updateVideoPlaybackRate() {
      if (!this.video || !Number.isFinite(this.video.duration) || this.video.duration <= 0) return;

      if (this.video.loop) {
        if (this.video.playbackRate !== 1) this.video.playbackRate = 1;
        return;
      }

      const tailStart = Math.max(0, this.video.duration - this.videoTailDuration);
      const progress = clamp((this.video.currentTime - tailStart) / this.videoTailDuration, 0, 1);
      const eased = progress * progress * (3 - 2 * progress);
      const targetRate = 1 - (1 - this.videoTailMinRate) * eased;

      if (Math.abs(this.video.playbackRate - targetRate) > 0.01) {
        this.video.playbackRate = targetRate;
      }
    }

    startVideoRateMonitor() {
      if (!this.video || !this.videoPlaybackAllowed || this.videoFrameCallback) return;
      if (this.video.loop) return;
      if (typeof this.video.requestVideoFrameCallback !== "function") return;

      const update = () => {
        this.videoFrameCallback = 0;
        this.updateVideoPlaybackRate();
        if (!this.videoCompleted) {
          this.videoFrameCallback = this.video.requestVideoFrameCallback(update);
        }
      };

      this.videoFrameCallback = this.video.requestVideoFrameCallback(update);
    }

    prepareNarrative() {
      if (!this.motionAllowed) {
        this.setStage("awake");
        return;
      }
      if (this.narrativeStarted) return;

      this.narrativeStarted = true;
      this.detailStartedAt = performance.now();
      this.setStage("detail");

      /* Two paints lock in the macro starting composition without a reverse flash. */
      this.primeFrame = window.requestAnimationFrame(() => {
        this.primeFrame = 0;
        this.readyFrame = window.requestAnimationFrame(() => {
          this.readyFrame = 0;
          this.root.classList.add("hero--stage-ready");
          this.startDetailMotion();

          this.scheduleNarrative(() => this.beginReveal(), this.detailHold);
          this.syncPlayback();
        });
      });
    }

    setStage(stage) {
      this.root.dataset.heroStage = stage;
    }

    beginReveal() {
      if (this.root.dataset.heroStage !== "detail") return;

      this.clearNarrativeTimer();
      this.stopAnimations(this.detailAnimations);
      this.stopAnimations(this.ambientAnimations);
      this.root.dataset.revealSource = "auto";
      this.setStage("reveal");

      const token = ++this.sequenceToken;
      const duration = this.revealDuration;
      const easing = "cubic-bezier(0.45, 0, 0.12, 1)";
      const styles = this.hero ? window.getComputedStyle(this.hero) : null;
      const detailScale = styles?.getPropertyValue("--camera-detail-scale").trim() || "2.25";
      const detailX = styles?.getPropertyValue("--camera-detail-x").trim() || "-21.8%";
      const detailY = styles?.getPropertyValue("--camera-detail-y").trim() || "6%";
      const animations = [
        this.animateTracked(
          this.camera,
          [
            {
              transform: `translate3d(${detailX}, ${detailY}, 0) scale(${detailScale})`,
            },
            {
              transform: "translate3d(-17.2%, 3.2%, 0) scale(1.88)",
              offset: 0.34,
            },
            {
              transform: "translate3d(-5.4%, 0.9%, 0) scale(1.28)",
              offset: 0.74,
            },
            {
              transform: "translate3d(0, 0, 0) scale(1)",
            },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.monoPicture,
          [
            {
              filter: "grayscale(0) blur(0.25px) saturate(1.015) contrast(1.02) brightness(0.88)",
              opacity: 1,
            },
            {
              filter: "grayscale(0.62) blur(0.15px) contrast(1.055) brightness(0.78)",
              opacity: 1,
              offset: 0.68,
            },
            {
              filter: "grayscale(1) contrast(1.08) brightness(0.72)",
              opacity: 1,
            },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.colorPicture,
          [
            {
              "--wake-core": "140%",
              "--wake-edge": "155%",
              clipPath: "circle(155% at var(--focus-x) var(--focus-y))",
              filter: "saturate(1.025) contrast(1.015) brightness(1)",
              opacity: 1,
            },
            {
              "--wake-core": "12%",
              "--wake-edge": "22%",
              clipPath: "circle(22% at var(--focus-x) var(--focus-y))",
              filter: "saturate(1.07) contrast(1.025) brightness(1.02)",
              opacity: 1,
            },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.petalCaustic,
          [
            { opacity: 0.5, transform: "translate3d(-58%, -48%, 0) scale(1.08)" },
            { opacity: 0.2, transform: "translate3d(-48%, -54%, 0) scale(1.18)", offset: 0.58 },
            { opacity: 0, transform: "translate3d(-42%, -56%, 0) scale(1.24)" },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.washLayer,
          [{ opacity: 0.36 }, { opacity: 0.86 }],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.lightLayer,
          [
            { opacity: 0.12, transform: "translate3d(22%, 0, 0)" },
            { opacity: 0.16, transform: "translate3d(12%, 0, 0)" },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
      ];

      Promise.all(animations).then((completed) => {
        if (
          token === this.sequenceToken &&
          this.root.dataset.heroStage === "reveal" &&
          completed.every(Boolean)
        ) {
          this.beginAwaken();
        }
      });
      this.syncPlayback();
    }

    beginAwaken() {
      this.setStage("awake");
      const token = ++this.sequenceToken;
      const duration = this.awakenDuration;
      const easing = "cubic-bezier(0.32, 0, 0.18, 1)";
      const animations = [
        this.animateTracked(
          this.monoPicture,
          [
            { filter: "grayscale(1) contrast(1.08) brightness(0.72)", opacity: 1 },
            { filter: "grayscale(1) contrast(1.02) brightness(0.92)", opacity: 1 },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.colorPicture,
          [
            {
              "--wake-core": "12%",
              "--wake-edge": "22%",
              clipPath: "circle(22% at var(--focus-x) var(--focus-y))",
              filter: "saturate(1.07) contrast(1.025) brightness(1.02)",
              opacity: 1,
            },
            {
              "--wake-core": "140%",
              "--wake-edge": "155%",
              clipPath: "circle(155% at var(--focus-x) var(--focus-y))",
              filter: "saturate(1.02) contrast(1.01) brightness(1)",
              opacity: 1,
            },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.wakeBloom,
          [
            { opacity: 0, transform: "translate3d(-50%, -50%, 0) scale(0.24)" },
            { opacity: 0.58, transform: "translate3d(-50%, -50%, 0) scale(1.75)", offset: 0.38 },
            { opacity: 0, transform: "translate3d(-50%, -50%, 0) scale(5.8)" },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.washLayer,
          [{ opacity: 0.86 }, { opacity: 0.82 }],
          { duration, easing },
          this.sequenceAnimations
        ),
        this.animateTracked(
          this.lightLayer,
          [
            { opacity: 0.16, transform: "translate3d(12%, 0, 0)" },
            { opacity: 0.34, transform: "translate3d(-8%, 0, 0)" },
          ],
          { duration, easing },
          this.sequenceAnimations
        ),
      ];

      Promise.all(animations).then((completed) => {
        if (
          token === this.sequenceToken &&
          this.root.dataset.heroStage === "awake" &&
          completed.every(Boolean)
        ) {
          this.root.classList.add("hero--narrative-complete");
          this.startAmbientMotion();
        }
      });
      this.syncPlayback();
    }

    startDetailMotion() {
      if (!this.motionAllowed || this.root.dataset.heroStage !== "detail") return;

      const styles = this.hero ? window.getComputedStyle(this.hero) : null;
      const detailScale = parseNumber(
        styles?.getPropertyValue("--camera-detail-scale"),
        2.25
      );
      const detailX = styles?.getPropertyValue("--camera-detail-x").trim() || "-21.8%";
      const detailY = styles?.getPropertyValue("--camera-detail-y").trim() || "6%";

      this.createLoop(
        this.camera,
        [
          {
            transform: `translate3d(${detailX}, ${detailY}, 0) scale(${(detailScale * 0.997).toFixed(3)})`,
          },
          {
            transform: `translate3d(${detailX}, ${detailY}, 0) scale(${(detailScale * 1.011).toFixed(3)})`,
          },
        ],
        { duration: 2600, easing: "ease-in-out", direction: "alternate" },
        this.detailAnimations
      );

      this.createLoop(
        this.colorPicture,
        [
          { filter: "saturate(1.01) contrast(1.012) brightness(0.985)" },
          { filter: "saturate(1.035) contrast(1.025) brightness(1.025)" },
        ],
        { duration: 2400, easing: "ease-in-out", direction: "alternate" },
        this.detailAnimations
      );

      this.createLoop(
        this.petalCaustic,
        [
          { opacity: 0.28, transform: "translate3d(-62%, -45%, 0) scale(1.04)" },
          { opacity: 0.58, transform: "translate3d(-42%, -54%, 0) scale(1.14)" },
        ],
        { duration: 2600, easing: "ease-in-out", direction: "alternate" },
        this.detailAnimations
      );
    }

    startAmbientMotion() {
      if (!this.motionAllowed || this.root.dataset.heroStage !== "awake") return;
      this.createLoop(
        this.lightLayer,
        [
          { opacity: 0.23, transform: "translate3d(-4%, 0, 0)" },
          { opacity: 0.34, transform: "translate3d(-13%, 0, 0)" },
        ],
        { duration: 20000, easing: "ease-in-out", direction: "alternate" },
        this.ambientAnimations
      );
    }

    animateTracked(element, keyframes, options, collection) {
      if (!element || typeof element.animate !== "function") return Promise.resolve(true);

      const animation = element.animate(keyframes, {
        fill: "both",
        iterations: 1,
        ...options,
      });
      collection.add(animation);
      if (!this.shouldPlayMotion()) animation.pause();

      return animation.finished.then(
        () => {
          collection.delete(animation);
          animation.cancel();
          return true;
        },
        () => {
          collection.delete(animation);
          return false;
        }
      );
    }

    createLoop(element, keyframes, options, collection) {
      if (!element || typeof element.animate !== "function") return null;
      const animation = element.animate(keyframes, {
        fill: "both",
        iterations: Infinity,
        ...options,
      });
      collection.add(animation);
      if (!this.shouldPlayMotion()) animation.pause();
      return animation;
    }

    stopAnimations(collection) {
      collection.forEach((animation) => animation.cancel());
      collection.clear();
    }

    forEachAnimation(callback) {
      [this.detailAnimations, this.sequenceAnimations, this.ambientAnimations].forEach((collection) => {
        collection.forEach(callback);
      });
    }

    scheduleNarrative(task, delay) {
      this.clearNarrativeTimer();
      this.narrativeTask = task;
      this.narrativeRemaining = Math.max(0, delay);
      if (this.shouldPlayMotion()) this.resumeNarrativeTimer();
    }

    resumeNarrativeTimer() {
      if (this.narrativeTimer || !this.narrativeTask) return;
      this.narrativeDeadline = performance.now() + this.narrativeRemaining;
      this.narrativeTimer = window.setTimeout(() => {
        this.narrativeTimer = 0;
        this.narrativeDeadline = 0;
        const task = this.narrativeTask;
        this.narrativeTask = null;
        this.narrativeRemaining = 0;
        if (task) task();
      }, this.narrativeRemaining);
    }

    pauseNarrativeTimer() {
      if (!this.narrativeTimer) return;
      window.clearTimeout(this.narrativeTimer);
      this.narrativeTimer = 0;
      this.narrativeRemaining = Math.max(0, this.narrativeDeadline - performance.now());
      this.narrativeDeadline = 0;
    }

    clearNarrativeTimer() {
      if (this.narrativeTimer) window.clearTimeout(this.narrativeTimer);
      this.narrativeTimer = 0;
      this.narrativeDeadline = 0;
      this.narrativeRemaining = 0;
      this.narrativeTask = null;
    }

    onVisibilityChange() {
      this.pageIsVisible = !document.hidden;
      this.syncPlayback();
    }

    shouldPlayMotion() {
      return this.motionAllowed && this.isVisible && this.pageIsVisible;
    }

    shouldPlayVideo() {
      return this.videoPlaybackAllowed && !this.videoCompleted && this.isVisible && this.pageIsVisible;
    }

    syncPlayback() {
      const shouldPlay = this.shouldPlayMotion();
      this.root.classList.toggle("hero--paused", !shouldPlay);

      this.forEachAnimation((animation) => {
        if (shouldPlay) {
          animation.play();
        } else {
          animation.pause();
        }
      });

      if (shouldPlay) {
        this.resumeNarrativeTimer();
      } else {
        this.pauseNarrativeTimer();
      }

      if (this.video) {
        if (this.shouldPlayVideo()) {
          const promise = this.video.play();
          if (promise && typeof promise.catch === "function") promise.catch(() => {});
        } else {
          this.video.pause();
        }
      }

      if (shouldPlay && this.pointerMotionAllowed) {
        this.startFrame();
      } else {
        this.stopFrame();
      }
    }

    cachePointerRect() {
      if (this.hero) this.pointerRect = this.hero.getBoundingClientRect();
    }

    onPointerMove(event) {
      if (!this.pointerRect) this.cachePointerRect();
      if (!this.pointerRect || !this.pointerRect.width || !this.pointerRect.height) return;

      const normalizedX = (event.clientX - this.pointerRect.left) / this.pointerRect.width - 0.5;
      const normalizedY = (event.clientY - this.pointerRect.top) / this.pointerRect.height - 0.5;

      this.targetX = clamp(normalizedX * -2 * this.parallaxStrength, -this.parallaxStrength, this.parallaxStrength);
      this.targetY = clamp(normalizedY * -1.25 * this.parallaxStrength, -this.parallaxStrength, this.parallaxStrength);
      this.startFrame();
    }

    resetPointer() {
      this.targetX = 0;
      this.targetY = 0;
      this.startFrame();
    }

    startFrame() {
      if (this.frame || !this.pointerMotionAllowed || !this.isVisible || !this.pageIsVisible) return;
      this.frame = window.requestAnimationFrame(() => this.updateFrame());
    }

    updateFrame() {
      this.frame = 0;
      this.currentX += (this.targetX - this.currentX) * 0.075;
      this.currentY += (this.targetY - this.currentY) * 0.075;

      if (this.hero) {
        this.hero.style.setProperty("--hero-shift-x", `${this.currentX.toFixed(2)}px`);
        this.hero.style.setProperty("--hero-shift-y", `${this.currentY.toFixed(2)}px`);
      }

      const moving =
        Math.abs(this.targetX - this.currentX) > 0.03 || Math.abs(this.targetY - this.currentY) > 0.03;
      if (moving) this.startFrame();
    }

    stopFrame() {
      if (!this.frame) return;
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    select() {
      this.root.classList.remove("hero--paused");
      if (this.root.scrollIntoView) {
        this.root.scrollIntoView({ behavior: this.motionAllowed ? "smooth" : "auto", block: "start" });
      }
    }

    deselect() {
      this.syncPlayback();
    }

    destroy() {
      this.stopFrame();
      if (this.primeFrame) window.cancelAnimationFrame(this.primeFrame);
      if (this.readyFrame) window.cancelAnimationFrame(this.readyFrame);
      if (this.videoFrameCallback && this.video && typeof this.video.cancelVideoFrameCallback === "function") {
        this.video.cancelVideoFrameCallback(this.videoFrameCallback);
        this.videoFrameCallback = 0;
      }
      this.clearNarrativeTimer();
      this.sequenceToken += 1;
      this.stopAnimations(this.detailAnimations);
      this.stopAnimations(this.sequenceAnimations);
      this.stopAnimations(this.ambientAnimations);
      this.abortController.abort();
      if (this.observer) this.observer.disconnect();
      if (this.video) {
        this.video.pause();
        this.video.classList.remove("is-ready");
      }

      if (this.hero) {
        this.hero.style.removeProperty("--hero-shift-x");
        this.hero.style.removeProperty("--hero-shift-y");
      }

      this.root.classList.remove(
        "hero--enhanced",
        "hero--static",
        "hero--paused",
        "hero--loaded",
        "hero--video-ready",
        "hero--video-complete",
        "hero--stage-ready",
        "hero--narrative-complete"
      );
      delete this.root.dataset.heroStage;
      delete this.root.dataset.revealSource;
      this.root.style.removeProperty("--detail-hold");
      this.root.style.removeProperty("--reveal-duration");
      this.root.style.removeProperty("--awaken-duration");
      delete this.root[INSTANCE_KEY];
      instances.delete(this);
    }
  }

  class SiteNavigation {
    constructor(header) {
      this.header = header;
      this.toggle = header.querySelector("[data-menu-toggle]");
      this.panel = header.querySelector("[data-mobile-nav]");
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.scrollFrame = 0;
      this.isOpen = false;

      window.addEventListener("scroll", () => this.requestScrollSync(), {
        passive: true,
        signal: this.signal,
      });

      if (this.toggle && this.panel) {
        this.panel.inert = true;
        this.toggle.addEventListener("click", () => this.setOpen(!this.isOpen), {
          signal: this.signal,
        });
        this.panel.addEventListener("click", (event) => {
          if (event.target.closest("a")) this.setOpen(false);
        }, { signal: this.signal });
        document.addEventListener("keydown", (event) => {
          if (event.key === "Escape" && this.isOpen) {
            this.setOpen(false);
            this.toggle.focus();
          }
        }, { signal: this.signal });
      }

      this.syncScrolledState();
    }

    requestScrollSync() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.syncScrolledState();
      });
    }

    syncScrolledState() {
      this.header.classList.toggle("site-header--scrolled", window.scrollY > 20 && !this.isOpen);
    }

    setOpen(open) {
      if (!this.toggle || !this.panel) return;
      this.isOpen = Boolean(open);
      this.header.classList.toggle("site-header--menu-open", this.isOpen);
      this.toggle.setAttribute("aria-expanded", String(this.isOpen));
      this.toggle.setAttribute("aria-label", this.isOpen ? "Close menu" : "Open menu");
      this.panel.setAttribute("aria-hidden", String(!this.isOpen));
      this.panel.inert = !this.isOpen;
      document.body.classList.toggle("menu-is-open", this.isOpen);
      this.syncScrolledState();
    }

    destroy() {
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.setOpen(false);
      this.abortController.abort();
      delete this.header[NAV_INSTANCE_KEY];
    }
  }

  function initHero(root) {
    if (!(root instanceof Element)) return null;
    if (root[INSTANCE_KEY]) root[INSTANCE_KEY].destroy();

    const instance = new FloralHero(root);
    root[INSTANCE_KEY] = instance;
    instances.add(instance);
    return instance;
  }

  function initWithin(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initHero(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(initHero);
  }

  function destroyWithin(scope) {
    [...instances].forEach((instance) => {
      if (scope === instance.root || (scope.contains && scope.contains(instance.root))) {
        instance.destroy();
      }
    });
  }

  function initNavigation(scope = document) {
    const header = scope.querySelector && scope.querySelector("[data-site-header]");
    if (!header || header[NAV_INSTANCE_KEY]) return;
    header[NAV_INSTANCE_KEY] = new SiteNavigation(header);
  }

  function boot() {
    initWithin(document);
    initNavigation(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  /* Shopify Online Store 2.0 Theme Editor lifecycle hooks. */
  document.addEventListener("shopify:section:load", (event) => {
    initWithin(event.target);
    initNavigation(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyWithin(event.target);
    const header = event.target.querySelector && event.target.querySelector("[data-site-header]");
    if (header && header[NAV_INSTANCE_KEY]) header[NAV_INSTANCE_KEY].destroy();
  });

  document.addEventListener("shopify:section:select", (event) => {
    const root = event.target.matches && event.target.matches(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);
    if (root && root[INSTANCE_KEY]) root[INSTANCE_KEY].select();
  });

  document.addEventListener("shopify:section:deselect", (event) => {
    const root = event.target.matches && event.target.matches(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);
    if (root && root[INSTANCE_KEY]) root[INSTANCE_KEY].deselect();
  });

  window.FloralHero = Object.freeze({
    init: initHero,
    initWithin,
    destroyWithin,
  });
})();
